import React, { useState } from 'react';
import { API_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Syringe,
  CheckCircle2,
  AlertTriangle,
  Stethoscope,
  User,
  Activity,
  Shield,
  Zap,
  Wind,
  AlertOctagon,
  Brain,
  Clock,
  Calculator,
  Sparkles,
  HelpCircle,
  Info
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

export default function SedationProceduralePage() {
  const [checkedItems, setCheckedItems] = useState({});
  const [patientData, setPatientData] = useState({
    age: '',
    weight: '',
    asa: '',
    hasAllergies: false,
    hasVMD: false,
    hasID: false,
    isPolytrauma: false,
    isHypovolemic: false,
    procedureType: '',
    hasJeun: false
  });
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showASAModal, setShowASAModal] = useState(false);
  const [asaCriteria, setAsaCriteria] = useState({
    asa1: false,
    asa2_diabetes: false,
    asa2_hta: false,
    asa2_tabac: false,
    asa2_obesite: false,
    asa2_grossesse: false,
    asa3_diabetes_complications: false,
    asa3_irc: false,
    asa3_bpco: false,
    asa3_imc_sup40: false,
    asa3_cardiopathie: false,
    asa3_avc: false,
    asa4_im_recent: false,
    asa4_avc_recent: false,
    asa4_choc: false,
    asa4_insuffisance: false
  });

  const handleCheck = (section, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${section}-${item}`]: !prev[`${section}-${item}`]
    }));
  };

  const handlePatientDataChange = (field, value) => {
    setPatientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateASAScore = () => {
    // ASA 4 - Conditions critiques
    if (asaCriteria.asa4_im_recent || asaCriteria.asa4_avc_recent || 
        asaCriteria.asa4_choc || asaCriteria.asa4_insuffisance) {
      return '4';
    }
    
    // ASA 3 - Maladie systémique sévère
    if (asaCriteria.asa3_diabetes_complications || asaCriteria.asa3_irc || 
        asaCriteria.asa3_bpco || asaCriteria.asa3_imc_sup40 || 
        asaCriteria.asa3_cardiopathie || asaCriteria.asa3_avc) {
      return '3';
    }
    
    // ASA 2 - Maladie systémique légère
    if (asaCriteria.asa2_diabetes || asaCriteria.asa2_hta || 
        asaCriteria.asa2_tabac || asaCriteria.asa2_obesite || 
        asaCriteria.asa2_grossesse) {
      return '2';
    }
    
    // ASA 1 - Patient en bonne santé
    if (asaCriteria.asa1) {
      return '1';
    }
    
    return '';
  };

  const applyASAScore = () => {
    const calculatedScore = calculateASAScore();
    if (calculatedScore) {
      handlePatientDataChange('asa', calculatedScore);
      setShowASAModal(false);
      toast.success(`Score ASA ${calculatedScore} appliqué`);
    } else {
      toast.error('Veuillez sélectionner au moins un critère');
    }
  };

  const resetASACriteria = () => {
    setAsaCriteria({
      asa1: false,
      asa2_diabetes: false,
      asa2_hta: false,
      asa2_tabac: false,
      asa2_obesite: false,
      asa2_grossesse: false,
      asa3_diabetes_complications: false,
      asa3_irc: false,
      asa3_bpco: false,
      asa3_imc_sup40: false,
      asa3_cardiopathie: false,
      asa3_avc: false,
      asa4_im_recent: false,
      asa4_avc_recent: false,
      asa4_choc: false,
      asa4_insuffisance: false
    });
  };

  const calculateDose = (drug, weight, age) => {
    if (!weight || !age) return null;
    
    const w = parseFloat(weight);
    const a = parseFloat(age);
    
    switch(drug) {
      case 'propofol':
        if (a > 70) {
          return `${100 - a} mg (100 mg MOINS l'âge)`;
        }
        return `${(w * 0.8).toFixed(1)} - ${(w * 1).toFixed(1)} mg (0,8-1 mg/kg)`;
      
      case 'propofol-cardioversion':
        const dose1 = 100 - a;
        const dose2 = (w * 0.5).toFixed(1);
        return `${Math.min(dose1, dose2)} mg (choisir la plus petite: ${dose1} mg ou ${dose2} mg)`;
      
      case 'ketofol-ketamine':
        return `${(w * 0.5).toFixed(1)} mg (0,5 mg/kg)`;
      
      case 'ketofol-propofol':
        return `${(w * 0.5).toFixed(1)} mg initial (0,5 mg/kg)`;
      
      case 'esketamine':
        return `${(w * 0.25).toFixed(1)} - ${(w * 0.5).toFixed(1)} mg (0,25-0,5 mg/kg)`;
      
      case 'ketamine':
        return `${(w * 0.8).toFixed(1)} - ${(w * 1).toFixed(1)} mg (0,8-1 mg/kg)`;
      
      default:
        return null;
    }
  };

  const getDrugRecommendation = () => {
    const { asa, hasVMD, hasID, isPolytrauma, isHypovolemic, weight, age } = patientData;
    
    // ASA 4, polytraumatisé, hypovolémique -> Eskétamine ou Kétamine
    if (asa === '4' || isPolytrauma || isHypovolemic || (hasVMD && hasID)) {
      return {
        drug: 'esketamine-ketamine',
        name: 'Eskétamine ou Kétamine seule',
        reason: 'Patient ASA 4, polytraumatisé ou hypovolémique',
        esketamineDose: calculateDose('esketamine', weight, age),
        ketamineDose: calculateDose('ketamine', weight, age)
      };
    }
    
    // ASA 3 ou possiblement hypovolémique -> Kétofol
    if (asa === '3') {
      return {
        drug: 'ketofol',
        name: 'KÉTOFOL (association)',
        reason: 'Patient ASA 3 - association pour sécurité',
        ketamineDose: calculateDose('ketofol-ketamine', weight, age),
        propofolDose: calculateDose('ketofol-propofol', weight, age)
      };
    }
    
    // Cardioversion urgente
    if (patientData.procedureType === 'cardioversion') {
      return {
        drug: 'propofol-cardioversion',
        name: 'Propofol titré (cardioversion)',
        reason: 'Cardioversion en urgence',
        dose: calculateDose('propofol-cardioversion', weight, age)
      };
    }
    
    // Adulte en bonne santé, ASA 1-2, BMI<30 -> Propofol
    if (asa === '1' || asa === '2') {
      return {
        drug: 'propofol',
        name: 'Propofol (1ère intention)',
        reason: 'Patient ASA 1-2, en bonne santé',
        dose: calculateDose('propofol', weight, age)
      };
    }
    
    return {
      drug: 'undetermined',
      name: 'Données insuffisantes',
      reason: 'Veuillez compléter les données patient'
    };
  };

  const handleAIAnalysis = async () => {
    if (!patientData.age || !patientData.weight || !patientData.asa) {
      toast.error('Veuillez renseigner au minimum l\'âge, le poids et le score ASA');
      return;
    }

    setAiAnalyzing(true);
    try {
      const backendUrl = API_URL;
      const response = await fetch(`${backendUrl}/api/ai/analyze-sedation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientData,
          checkedItems,
          recommendation: getDrugRecommendation()
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResult(data.analysis);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'analyse IA');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const recommendation = getDrugRecommendation();

  const checklistData = {
    generalites: {
      title: "01 - GÉNÉRALITÉS",
      icon: AlertTriangle,
      items: [
        { 
          id: "definition",
          label: "Objectif: Ramsay 4-5",
          detail: "Sédation de niveau Ramsay 4-5 en toute sécurité"
        },
        { 
          id: "alternatives",
          label: "Alternatives évaluées",
          detail: "ALR (bloc ilio-fascial), analgésie multimodale sans sédation"
        },
        { 
          id: "risques",
          label: "Risques connus: 2-5%",
          detail: "Dépression respiratoire, hypotension, agitation",
          important: true
        }
      ]
    },
    evaluation: {
      title: "02 - ÉVALUATION DU PATIENT",
      icon: User,
      items: [
        { 
          id: "cardio",
          label: "Tolérance cardio-respiratoire",
          detail: "Âge, atcd, comorbidités (ASA), BMI, toxiques, dépresseurs respiratoires"
        },
        { 
          id: "volemie",
          label: "Volémie (SI) et mécanisme lésionnel",
          detail: "Évaluation de l'état volumique"
        },
        { 
          id: "vmd",
          label: "Critères de VMD, ID ou allergie aux sédatifs",
          detail: "Voies aériennes difficiles"
        },
        { 
          id: "jeun",
          label: "Statut à jeun",
          detail: "Si urgence justifiée, absence de jeûne n'est pas une CI"
        }
      ]
    },
    preparation: {
      title: "03 - PRÉPARATION DU PATIENT (OBLIGATOIRE)",
      icon: Shield,
      items: [
        { 
          id: "position",
          label: "Patient installé demi-assis si possible",
          detail: "Position de sécurité"
        },
        { 
          id: "equipe",
          label: "Équipe prête",
          detail: "Personnel formé disponible"
        },
        { 
          id: "monitoring",
          label: "Monitorage indispensable",
          detail: "Scope, PNI / 3-5 min, SpO2, ETCO2 (lunettes)",
          important: true
        },
        { 
          id: "reanimation",
          label: "Matériel de réanimation vérifié et prêt",
          detail: "BAVU branché, aspiration prête, plateau IOT à proximité",
          important: true
        },
        { 
          id: "oxygen",
          label: "O2 ≥ 15 L/min MHC",
          detail: "Débuter 3-5 min avant, maintenir jusqu'au réveil",
          important: true
        },
        { 
          id: "vvp",
          label: "VVP fiable + remplissage vasculaire",
          detail: "Surtout si propofol utilisé"
        },
        { 
          id: "communication",
          label: "Communication thérapeutique",
          detail: "Rassurer le patient"
        }
      ]
    },
    surveillance: {
      title: "05 - SURVEILLANCE & GESTION",
      icon: Activity,
      items: [
        { 
          id: "etco2",
          label: "Surveillance ETCO2",
          detail: "Permet d'anticiper une apnée",
          important: true
        },
        { 
          id: "subluxation",
          label: "En cas d'apnée: Subluxation mandibulaire",
          detail: "Privilégier à la canule de Guédel"
        },
        { 
          id: "bavu",
          label: "Ventilation BAVU si nécessaire",
          detail: "Seulement si désaturation marquée malgré stimulation et subluxation"
        },
        { 
          id: "morphine-midazolam",
          label: "❌ NE PAS associer morphine + midazolam",
          detail: "Sédation de mauvaise qualité, risque de complications",
          important: true
        }
      ]
    }
  };

  const drugDetails = {
    propofol: {
      name: "Propofol (1ère intention)",
      dosage: "0,8 à 1 mg/kg IV (ou 100 mg MOINS l'âge si >70 ans)",
      administration: "Puis ½ dose / 3 min si besoin jusqu'à Ramsay 5",
      indications: [
        "Adulte en bonne santé, ASA 1 ou 2, BMI < 30",
        "≤ 2 critères de VMD ou d'ID",
        "Soins douloureux courts +/- myorelaxation",
        "Pas d'hypovolémie marquée (SI < 0,9)",
        "Pas d'imprégnation morphinique marquée (<10 mg)"
      ],
      precautions: [
        "Praticien formé à l'utilisation du propofol",
        "Surveillance rapprochée et continue possible"
      ],
      color: "bg-cyan-500/10 border-cyan-500/30"
    },
    ketofol: {
      name: "KÉTOFOL (Association)",
      dosage: "Kétamine 0,5 mg/kg IVL + Propofol titré (0,5 mg/kg puis ½ dose / 3 min)",
      administration: "Utiliser 2 seringues : Kétamine puis Propofol",
      indications: [
        "Patient ASA 3",
        "Possiblement hypovolémique",
        "Recherche effet hypertenseur et antalgique (kétamine)",
        "Effets myorelaxant et antiémétique (propofol)"
      ],
      precautions: [
        "Propofol seul possible mais délicat dans ces situations"
      ],
      color: "bg-amber-500/10 border-amber-500/30"
    },
    esketamine: {
      name: "Eskétamine seule",
      dosage: "0,25 à 0,5 mg/kg IVL sur 30 s",
      administration: "½ dose supplémentaire si besoin d'une sédation plus longue",
      indications: [
        "Patient ASA 4",
        "Polytraumatisé ou hypovolémie marquée avérée",
        "Plus de 2 critères de VMD ou d'ID",
        "Soin douloureux ++ ou long (plusieurs réductions)",
        "Moins d'effets psychodysleptiques attendus"
      ],
      precautions: [],
      color: "bg-emerald-500/10 border-emerald-500/30"
    },
    ketamine: {
      name: "Kétamine seule",
      dosage: "0,8 à 1 mg/kg IVL sur 30 s",
      administration: "½ dose supplémentaire si besoin d'une sédation plus longue",
      indications: [
        "Patient ASA 4",
        "Polytraumatisé ou hypovolémie marquée avérée",
        "Plus de 2 critères de VMD ou d'ID",
        "Soin douloureux ++ ou long (plusieurs réductions)"
      ],
      precautions: [],
      color: "bg-green-500/10 border-green-500/30"
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="sedation-procedurale-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Syringe className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Sédation Procédurale
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Protocole complet pour une sédation sécurisée (Ramsay 4-5) avec aide au choix du sédatif et calcul de dose
          </p>
        </div>

        {/* Données Patient & Recommandation */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Données Patient */}
          <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="patient-data-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <User className="w-5 h-5" />
                Données Patient
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="age" className="text-sm text-[hsl(var(--text))]">Âge (ans)</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Ex: 45"
                    value={patientData.age}
                    onChange={(e) => handlePatientDataChange('age', e.target.value)}
                    className="mt-1"
                    data-testid="patient-age-input"
                  />
                </div>
                <div>
                  <Label htmlFor="weight" className="text-sm text-[hsl(var(--text))]">Poids (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="Ex: 75"
                    value={patientData.weight}
                    onChange={(e) => handlePatientDataChange('weight', e.target.value)}
                    className="mt-1"
                    data-testid="patient-weight-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="asa" className="text-sm text-[hsl(var(--text))]">Score ASA</Label>
                  <Dialog open={showASAModal} onOpenChange={setShowASAModal}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-cyan-400 hover:text-cyan-300"
                        data-testid="asa-helper-button"
                      >
                        <HelpCircle className="w-3 h-3 mr-1" />
                        Aide au calcul
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
                      <DialogHeader>
                        <DialogTitle className="text-cyan-300 flex items-center gap-2">
                          <Calculator className="w-5 h-5" />
                          Classification ASA (American Society of Anesthesiologists)
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        {/* ASA 1 */}
                        <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-emerald-500 text-black">ASA 1</Badge>
                            <span className="font-semibold text-emerald-300">Patient en bonne santé</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id="asa1"
                              checked={asaCriteria.asa1}
                              onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa1: checked}))}
                            />
                            <Label htmlFor="asa1" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                              Aucune maladie organique, physiologique, biochimique ou psychiatrique
                            </Label>
                          </div>
                        </div>

                        {/* ASA 2 */}
                        <div className="p-4 rounded-lg border border-cyan-500/30 bg-cyan-500/5">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-cyan-500 text-black">ASA 2</Badge>
                            <span className="font-semibold text-cyan-300">Maladie systémique légère</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa2_diabetes"
                                checked={asaCriteria.asa2_diabetes}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa2_diabetes: checked}))}
                              />
                              <Label htmlFor="asa2_diabetes" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Diabète bien contrôlé (sans complications)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa2_hta"
                                checked={asaCriteria.asa2_hta}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa2_hta: checked}))}
                              />
                              <Label htmlFor="asa2_hta" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                HTA bien contrôlée
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa2_tabac"
                                checked={asaCriteria.asa2_tabac}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa2_tabac: checked}))}
                              />
                              <Label htmlFor="asa2_tabac" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Tabagisme actif
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa2_obesite"
                                checked={asaCriteria.asa2_obesite}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa2_obesite: checked}))}
                              />
                              <Label htmlFor="asa2_obesite" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Obésité modérée (IMC 30-40)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa2_grossesse"
                                checked={asaCriteria.asa2_grossesse}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa2_grossesse: checked}))}
                              />
                              <Label htmlFor="asa2_grossesse" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Grossesse normale
                              </Label>
                            </div>
                          </div>
                        </div>

                        {/* ASA 3 */}
                        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-amber-500 text-black">ASA 3</Badge>
                            <span className="font-semibold text-amber-300">Maladie systémique sévère</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_diabetes_complications"
                                checked={asaCriteria.asa3_diabetes_complications}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_diabetes_complications: checked}))}
                              />
                              <Label htmlFor="asa3_diabetes_complications" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Diabète avec complications (rétinopathie, néphropathie)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_irc"
                                checked={asaCriteria.asa3_irc}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_irc: checked}))}
                              />
                              <Label htmlFor="asa3_irc" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Insuffisance rénale chronique modérée
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_bpco"
                                checked={asaCriteria.asa3_bpco}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_bpco: checked}))}
                              />
                              <Label htmlFor="asa3_bpco" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                BPCO modérée à sévère
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_imc_sup40"
                                checked={asaCriteria.asa3_imc_sup40}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_imc_sup40: checked}))}
                              />
                              <Label htmlFor="asa3_imc_sup40" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Obésité morbide (IMC {'>'}40)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_cardiopathie"
                                checked={asaCriteria.asa3_cardiopathie}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_cardiopathie: checked}))}
                              />
                              <Label htmlFor="asa3_cardiopathie" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Cardiopathie stable (angor stable, IM {'>'} 6 mois)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa3_avc"
                                checked={asaCriteria.asa3_avc}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa3_avc: checked}))}
                              />
                              <Label htmlFor="asa3_avc" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                AVC ancien avec séquelles
                              </Label>
                            </div>
                          </div>
                        </div>

                        {/* ASA 4 */}
                        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-red-500 text-white">ASA 4</Badge>
                            <span className="font-semibold text-red-300">Maladie systémique sévère menaçant le pronostic vital</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa4_im_recent"
                                checked={asaCriteria.asa4_im_recent}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa4_im_recent: checked}))}
                              />
                              <Label htmlFor="asa4_im_recent" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Infarctus du myocarde récent ({'<'}3 mois)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa4_avc_recent"
                                checked={asaCriteria.asa4_avc_recent}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa4_avc_recent: checked}))}
                              />
                              <Label htmlFor="asa4_avc_recent" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                AVC récent ({'<'}3 mois)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa4_choc"
                                checked={asaCriteria.asa4_choc}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa4_choc: checked}))}
                              />
                              <Label htmlFor="asa4_choc" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                État de choc (hémorragique, septique, cardiogénique)
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="asa4_insuffisance"
                                checked={asaCriteria.asa4_insuffisance}
                                onCheckedChange={(checked) => setAsaCriteria(prev => ({...prev, asa4_insuffisance: checked}))}
                              />
                              <Label htmlFor="asa4_insuffisance" className="text-sm text-[hsl(var(--text-dim))] cursor-pointer">
                                Insuffisance cardiaque, respiratoire ou rénale terminale
                              </Label>
                            </div>
                          </div>
                        </div>

                        {/* Score calculé */}
                        <div className="p-4 rounded-lg border border-cyan-500/50 bg-cyan-500/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Info className="w-5 h-5 text-cyan-400" />
                              <span className="font-semibold text-cyan-300">Score ASA calculé :</span>
                            </div>
                            <Badge className="text-lg px-4 py-1 bg-cyan-500 text-black">
                              {calculateASAScore() || '?'}
                            </Badge>
                          </div>
                        </div>

                        {/* Boutons d'action */}
                        <div className="flex gap-3">
                          <Button
                            onClick={applyASAScore}
                            className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-black"
                            data-testid="apply-asa-button"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Appliquer le score
                          </Button>
                          <Button
                            onClick={resetASACriteria}
                            variant="outline"
                            className="border-[hsl(var(--border))] hover:bg-[hsl(var(--bg))]"
                          >
                            Réinitialiser
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <select
                  id="asa"
                  value={patientData.asa}
                  onChange={(e) => handlePatientDataChange('asa', e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-transparent border border-[hsl(var(--border))] rounded-md text-[hsl(var(--text))]"
                  data-testid="patient-asa-select"
                >
                  <option value="">Sélectionner...</option>
                  <option value="1">ASA 1</option>
                  <option value="2">ASA 2</option>
                  <option value="3">ASA 3</option>
                  <option value="4">ASA 4</option>
                </select>
              </div>

              <div>
                <Label htmlFor="procedureType" className="text-sm text-[hsl(var(--text))]">Type de procédure</Label>
                <select
                  id="procedureType"
                  value={patientData.procedureType}
                  onChange={(e) => handlePatientDataChange('procedureType', e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-transparent border border-[hsl(var(--border))] rounded-md text-[hsl(var(--text))]"
                  data-testid="procedure-type-select"
                >
                  <option value="">Sélectionner...</option>
                  <option value="reduction">Réduction fracture</option>
                  <option value="cardioversion">Cardioversion urgente</option>
                  <option value="drainage">Drainage</option>
                  <option value="suture">Suture complexe</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hasVMD"
                    checked={patientData.hasVMD}
                    onCheckedChange={(checked) => handlePatientDataChange('hasVMD', checked)}
                    data-testid="has-vmd-checkbox"
                  />
                  <Label htmlFor="hasVMD" className="text-sm cursor-pointer">
                    Critères de VMD (Voie aérienne difficile)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hasID"
                    checked={patientData.hasID}
                    onCheckedChange={(checked) => handlePatientDataChange('hasID', checked)}
                    data-testid="has-id-checkbox"
                  />
                  <Label htmlFor="hasID" className="text-sm cursor-pointer">
                    Critères d'ID (Intubation difficile)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isPolytrauma"
                    checked={patientData.isPolytrauma}
                    onCheckedChange={(checked) => handlePatientDataChange('isPolytrauma', checked)}
                    data-testid="is-polytrauma-checkbox"
                  />
                  <Label htmlFor="isPolytrauma" className="text-sm cursor-pointer">
                    Polytraumatisé
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isHypovolemic"
                    checked={patientData.isHypovolemic}
                    onCheckedChange={(checked) => handlePatientDataChange('isHypovolemic', checked)}
                    data-testid="is-hypovolemic-checkbox"
                  />
                  <Label htmlFor="isHypovolemic" className="text-sm cursor-pointer">
                    Hypovolémie marquée avérée
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hasAllergies"
                    checked={patientData.hasAllergies}
                    onCheckedChange={(checked) => handlePatientDataChange('hasAllergies', checked)}
                    data-testid="has-allergies-checkbox"
                  />
                  <Label htmlFor="hasAllergies" className="text-sm cursor-pointer">
                    Allergie aux sédatifs
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommandation */}
          <Card className={`bg-[hsl(var(--bg-elev))] border-2 ${recommendation.drug === 'undetermined' ? 'border-[hsl(var(--border))]' : 'border-cyan-500/50'}`} data-testid="drug-recommendation-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Calculator className="w-5 h-5" />
                Recommandation Sédatif
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {recommendation.drug === 'undetermined' ? (
                <Alert className="border-amber-500/30 bg-amber-500/10">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <AlertDescription className="text-amber-200">
                    Veuillez renseigner l'âge, le poids et le score ASA pour obtenir une recommandation
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                    <h3 className="text-lg font-semibold text-cyan-300 mb-2">{recommendation.name}</h3>
                    <p className="text-sm text-[hsl(var(--text-dim))] mb-3">{recommendation.reason}</p>
                    
                    {recommendation.drug === 'esketamine-ketamine' && (
                      <div className="space-y-2">
                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                          <p className="font-semibold text-emerald-300 mb-1">Eskétamine (moins d'effets psychodysleptiques)</p>
                          <p className="text-[hsl(var(--text))]">{recommendation.esketamineDose || 'Renseigner le poids'}</p>
                          <p className="text-xs text-[hsl(var(--text-dim))] mt-1">0,25-0,5 mg/kg IVL sur 30s</p>
                        </div>
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
                          <p className="font-semibold text-green-300 mb-1">Kétamine</p>
                          <p className="text-[hsl(var(--text))]">{recommendation.ketamineDose || 'Renseigner le poids'}</p>
                          <p className="text-xs text-[hsl(var(--text-dim))] mt-1">0,8-1 mg/kg IVL sur 30s</p>
                        </div>
                      </div>
                    )}
                    
                    {recommendation.drug === 'ketofol' && (
                      <div className="space-y-2">
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
                          <p className="font-semibold text-green-300 mb-1">1. Kétamine</p>
                          <p className="text-[hsl(var(--text))]">{recommendation.ketamineDose || 'Renseigner le poids'}</p>
                          <p className="text-xs text-[hsl(var(--text-dim))] mt-1">0,5 mg/kg IVL</p>
                        </div>
                        <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                          <p className="font-semibold text-cyan-300 mb-1">2. Propofol (titré)</p>
                          <p className="text-[hsl(var(--text))]">{recommendation.propofolDose || 'Renseigner le poids'}</p>
                          <p className="text-xs text-[hsl(var(--text-dim))] mt-1">Puis ½ dose / 3 min jusqu'à Ramsay 5</p>
                        </div>
                      </div>
                    )}
                    
                    {(recommendation.drug === 'propofol' || recommendation.drug === 'propofol-cardioversion') && (
                      <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                        <p className="font-semibold text-cyan-300 mb-1">Dose calculée</p>
                        <p className="text-[hsl(var(--text))] text-lg">{recommendation.dose || 'Renseigner âge et poids'}</p>
                        <p className="text-xs text-[hsl(var(--text-dim))] mt-1">Puis ½ dose / 3 min si besoin jusqu'à Ramsay {recommendation.drug === 'propofol-cardioversion' ? '4-5' : '5'}</p>
                      </div>
                    )}
                  </div>
                  
                  <Alert className="border-amber-500/30 bg-amber-500/10">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <AlertDescription className="text-amber-200 text-xs">
                      Aide cognitive pour le choix de la molécule et calcul de dose disponibles dans l'onglet "Scores"
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Checklist */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="sedation-checklist">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <CheckCircle2 className="w-5 h-5" />
              Checklist de Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {Object.entries(checklistData).map(([key, section]) => {
                const SectionIcon = section.icon;
                return (
                  <AccordionItem 
                    key={key} 
                    value={key}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`checklist-section-${key}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <SectionIcon className="w-5 h-5 text-cyan-400" />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-3 mt-3">
                        {section.items.map(item => (
                          <div 
                            key={item.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                              checkedItems[`${key}-${item.id}`]
                                ? 'bg-cyan-500/10 border-cyan-500/30'
                                : item.important
                                  ? 'bg-amber-500/5 border-amber-500/20'
                                  : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]'
                            }`}
                            data-testid={`checklist-item-${key}-${item.id}`}
                          >
                            <Checkbox
                              id={`${key}-${item.id}`}
                              checked={checkedItems[`${key}-${item.id}`] || false}
                              onCheckedChange={() => handleCheck(key, item.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label 
                                htmlFor={`${key}-${item.id}`}
                                className="font-medium text-[hsl(var(--text))] cursor-pointer block"
                              >
                                {item.label}
                              </label>
                              <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                              {item.important && (
                                <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Critique
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>

        {/* Détails des Sédatifs */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="sedative-details-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Syringe className="w-5 h-5" />
              04 - Choix du Sédatif - Détails Complets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(drugDetails).map(([key, drug]) => (
                <div 
                  key={key}
                  className={`p-4 rounded-lg border ${drug.color}`}
                  data-testid={`drug-detail-${key}`}
                >
                  <h3 className="text-lg font-semibold text-cyan-300 mb-2">{drug.name}</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Dosage:</p>
                      <p className="text-[hsl(var(--text-dim))] ml-2">{drug.dosage}</p>
                    </div>
                    
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Administration:</p>
                      <p className="text-[hsl(var(--text-dim))] ml-2">{drug.administration}</p>
                    </div>
                    
                    <div>
                      <p className="font-medium text-[hsl(var(--text))] mb-1">Indications:</p>
                      <ul className="list-disc list-inside text-[hsl(var(--text-dim))] ml-2 space-y-1">
                        {drug.indications.map((indication, idx) => (
                          <li key={idx}>{indication}</li>
                        ))}
                      </ul>
                    </div>
                    
                    {drug.precautions.length > 0 && (
                      <div>
                        <p className="font-medium text-[hsl(var(--text))] mb-1">Précautions:</p>
                        <ul className="list-disc list-inside text-[hsl(var(--text-dim))] ml-2 space-y-1">
                          {drug.precautions.map((precaution, idx) => (
                            <li key={idx}>{precaution}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Analyse IA */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="ai-analysis-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Brain className="w-5 h-5" />
              Analyse IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleAIAnalysis}
              disabled={aiAnalyzing}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
              data-testid="ai-analysis-button"
            >
              {aiAnalyzing ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyser avec l'IA
                </>
              )}
            </Button>

            {aiResult && (
              <Alert className="border-cyan-500/30 bg-cyan-500/10" data-testid="ai-result">
                <Brain className="w-4 h-4 text-cyan-400" />
                <AlertDescription className="text-[hsl(var(--text))] whitespace-pre-wrap mt-2">
                  {aiResult}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
